import type { LocalRow, SeedTables } from '../types/localDb.types'
import { USER_IDS } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'
import { skuId } from './wms-request.seed'
import { type FgActionName, type FgActorCode, type FgStatusCode, WF_FG } from './workflow-fg.seed'

/**
 * Inbound FG (C08) — ERD v010: fg_labeler_batch, fg_labeler_feed_log, fg_label, fg_putaway_task(+_item), fg_pack_stock,
 * pallet (kind FG), container, warehouse_zone (FG). Angka mengikuti prototype v7.44 (18 batch: 4 Ready, 1 Held,
 * 2 Dalam Produksi, 11 Putaway Completed; counter "Ready 4 · Held 1 · Putaway Completed 11").
 *
 * Kolom di luar ERD (diusulkan, laporan 12): `pallet.sku_id`, `pallet.occupied`; `fg_putaway_task_item.label_id`,
 * `done_qty`, `actual_pallet_id`, `actual_container_id`, `item_status`; `fg_putaway_task.source_ref`, `note`;
 * `fg_labeler_batch.label_ok`, `checked_at`.
 */

const utc = (at: string) => new Date(at.replace(' ', 'T') + ':00+07:00').toISOString()
const audit = (userId: string | null, at: string) => ({
  status: 'published',
  sort: null,
  user_created: userId,
  date_created: utc(at),
  user_updated: null,
  date_updated: null,
  user_deleted: null,
  date_deleted: null
})

export const FG_WAREHOUSE_ID = seedId(SEED_GROUP.warehouse, 2)
const ulil = USER_IDS.ulil
const budi = USER_IDS.budi

/* ------------------------------------------------------------ master SKU tambahan */

/** [kode, nama, tipe, btg/pack, bal/karton, slop/bal]. 1 Slop = 10 Pack. */
const EXTRA_SKUS = [
  ['AMANA-K12', 'Amana Kretek 12', 'SKM', 12, 4, 20],
  ['ARUM-MANIS', 'Arum Manis', 'SKT', 12, 4, 25],
  ['BRIGHTON-RED', 'Brighton Red', 'SKM', 20, 2, 25],
  ['MILLIONS', 'Millions', 'SKM', 20, 4, 20],
  ['TBC-BOLD-12', 'Tabaco Bold 12', 'SKM', 12, 4, 20]
] as const

const BASE_SKU_CODES = ['TBC-ONE-12', 'TBC-ONE-F16', 'TBC-BOLD-20', 'TBC-BOLD-20-SKT', 'TBC-XTRA-20'] as const
export const fgSkuId = (code: string) => {
  const extra = EXTRA_SKUS.findIndex(([c]) => c === code)
  return extra >= 0 ? seedId(SEED_GROUP.sku, 20 + extra) : skuId(code as (typeof BASE_SKU_CODES)[number])
}
const BAL_PER_KARTON: Record<string, number> = Object.fromEntries(EXTRA_SKUS.map(([c, , , , b]) => [c, b]))
const SLOP_PER_BAL: Record<string, number> = Object.fromEntries(EXTRA_SKUS.map(([c, , , , , s]) => [c, s]))
const balPerKarton = (sku: string) => BAL_PER_KARTON[sku] ?? 4
const slopPerBal = (sku: string) => SLOP_PER_BAL[sku] ?? 20
/** Pack per Karton / Bal / Slop. */
export const packPer = (sku: string) => {
  const bal = slopPerBal(sku) * 10
  return { karton: bal * balPerKarton(sku), bal, slop: 10 }
}

/* ------------------------------------------------------------ zona, pallet, container */

const ZONE_CODES = ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3', 'D1', 'D2', 'D3']
export const FG_STAGING_ZONE_CODE = 'ZS-01'
export const zoneId = (code: string) =>
  seedId(SEED_GROUP.warehouseZone, 100 + [...ZONE_CODES, FG_STAGING_ZONE_CODE].indexOf(code))

export const PALLETS = ZONE_CODES.flatMap(zone => [1, 2, 3, 4].map(n => `${zone}-P${n}`))
export const palletId = (code: string) => seedId(SEED_GROUP.pallet, 1000 + PALLETS.indexOf(code))

/** Isi pallet historis (stok yang sudah berdiri): [pallet, sku, jumlah Karton]. */
/** Elemen ke-4 opsional = jenis isi (`Bal` = pallet Bal lepas, C10); tanpa itu pallet berisi Karton. */
export const OPENING_PALLETS: [string, string, number, 'Bal'?][] = [
  ['A1-P1', 'TBC-ONE-12', 18],
  ['A1-P2', 'TBC-ONE-12', 18],
  ['A1-P3', 'TBC-ONE-12', 18],
  ['A1-P4', 'TBC-ONE-12', 8],
  ['A2-P1', 'TBC-BOLD-12', 18],
  ['B1-P1', 'TBC-BOLD-20', 18],
  ['B1-P2', 'TBC-BOLD-20', 18],
  ['B1-P3', 'TBC-BOLD-20', 4],
  ['B2-P1', 'TBC-XTRA-20', 24],
  ['B2-P2', 'TBC-XTRA-20', 24],
  ['B2-P3', 'TBC-XTRA-20', 24],
  ['B2-P4', 'TBC-XTRA-20', 24],
  ['B3-P1', 'TBC-XTRA-20', 6],
  ['C2-P1', 'TBC-BOLD-20-SKT', 11],
  ['C3-P1', 'MILLIONS', 18],
  ['C3-P2', 'MILLIONS', 18],
  ['C3-P3', 'MILLIONS', 16],
  ['D1-P1', 'BRIGHTON-RED', 18],
  ['D1-P2', 'BRIGHTON-RED', 4],
  ['D2-P1', 'ARUM-MANIS', 18],
  ['D2-P2', 'ARUM-MANIS', 18],
  ['D2-P3', 'ARUM-MANIS', 18],
  ['D2-P4', 'ARUM-MANIS', 18],
  ['D3-P1', 'ARUM-MANIS', 3],
  /* C10 — skenario konsolidasi: TBC-BOLD-12 terpencar di tiga pallet (8 + 6 + 4 = 18 karton → 1 pallet cukup). */
  ['D3-P2', 'TBC-BOLD-12', 8],
  ['D3-P3', 'TBC-BOLD-12', 6],
  ['D3-P4', 'TBC-BOLD-12', 4],
  /* C10 — pallet Bal lepas (label dibuat wms-merge.seed): isi = jumlah Bal. */
  ['A3-P1', 'TBC-BOLD-20', 2, 'Bal'],
  ['A3-P2', 'TBC-BOLD-20', 2, 'Bal'],
  ['A3-P3', 'TBC-BOLD-12', 3, 'Bal'],
  ['A3-P4', 'TBC-BOLD-12', 2, 'Bal']
]

const CONTAINERS = [
  ['CT-A', 'Container A', 'Slop'],
  ['CT-B', 'Container B', 'Slop'],
  ['CT-C', 'Container C', 'Pack']
] as const
const containerId = (code: string) => seedId(SEED_GROUP.container, CONTAINERS.findIndex(([c]) => c === code) + 1)

/* ------------------------------------------------------------ batch */

type BatchSpec = {
  no: string
  sku: string
  prodDate: string
  shift: number
  source: 'LINK' | 'BORONG' | 'ALM'
  machine?: string
  labelerRef?: string
  k: number
  b: number
  s: number
  p: number
  status: FgStatusCode
  receivedAt: string
  /** Langkah log submission: [aksi, aktor, oleh, waktu, alasan]. */
  steps?: [FgActionName, FgActorCode, string, string, string?][]
}

const BATCHES: BatchSpec[] = [
  {
    no: 'TB20T-20260818-0119-B001',
    sku: 'TBC-BOLD-20-SKT',
    prodDate: '2026-08-18',
    shift: 1,
    source: 'BORONG',
    machine: 'SASIB',
    labelerRef: 'ALF-260818-002',
    k: 3,
    b: 2,
    s: 2,
    p: 0,
    status: 'FG_READY_HANDOVER',
    receivedAt: '2026-08-18 14:50'
  },
  {
    no: 'TO12-20260818-0113-B001',
    sku: 'TBC-ONE-12',
    prodDate: '2026-08-18',
    shift: 1,
    source: 'LINK',
    machine: 'MOLLINS-1',
    labelerRef: 'ALF-260818-001',
    k: 12,
    b: 1,
    s: 0,
    p: 0,
    status: 'FG_READY_HANDOVER',
    receivedAt: '2026-08-18 14:45'
  },
  {
    no: 'AK12-20260818-0123-B001',
    sku: 'AMANA-K12',
    prodDate: '2026-08-18',
    shift: 1,
    source: 'ALM',
    k: 0,
    b: 10,
    s: 0,
    p: 0,
    status: 'FG_IN_PRODUCTION',
    receivedAt: '2026-08-18 14:20'
  },
  {
    no: 'AM12-20260818-0122-B001',
    sku: 'ARUM-MANIS',
    prodDate: '2026-08-18',
    shift: 1,
    source: 'ALM',
    k: 0,
    b: 0,
    s: 200,
    p: 0,
    status: 'FG_IN_PRODUCTION',
    receivedAt: '2026-08-18 14:10'
  },
  {
    no: 'TO12-20260818-0121-B001',
    sku: 'TBC-ONE-12',
    prodDate: '2026-08-18',
    shift: 1,
    source: 'ALM',
    labelerRef: 'ALM-TO12-20260818-0121-B001',
    k: 2,
    b: 2,
    s: 0,
    p: 0,
    status: 'FG_READY_HANDOVER',
    receivedAt: '2026-08-18 14:30'
  },
  {
    no: 'TX20-20260818-0114-B001',
    sku: 'TBC-XTRA-20',
    prodDate: '2026-08-18',
    shift: 1,
    source: 'LINK',
    k: 30,
    b: 2,
    s: 2,
    p: 0,
    status: 'FG_READY_HANDOVER',
    receivedAt: '2026-08-18 07:05'
  },
  {
    no: 'TB20T-20260817-0112-B001',
    sku: 'TBC-BOLD-20-SKT',
    prodDate: '2026-08-17',
    shift: 2,
    source: 'BORONG',
    k: 2,
    b: 1,
    s: 0,
    p: 0,
    status: 'FG_HELD',
    receivedAt: '2026-08-17 15:30',
    steps: [
      ['ACT_FG_SUBMIT', 'ACT_FG_ADMIN_FG', 'ulil', '2026-08-17 15:45'],
      [
        'ACT_FG_SPV_HOLD',
        'ACT_FG_SPV_FG',
        'hendra',
        '2026-08-17 16:30',
        'Cek fisik: kurang 1 Bal dari kiriman Auto Labeler'
      ]
    ]
  }
]

/** Batch historis yang stoknya sudah berdiri di pallet — tanpa label per unit (menghindari membengkaknya localStorage). */
export const HISTORIC: [string, string, string, number, string, number, number, number, number][] = [
  ['TO12-20260810-0103-B001', 'TBC-ONE-12', '2026-08-10', 1, 'LINK', 30, 1, 0, 0],
  ['TB20-20260815-0109-B001', 'TBC-BOLD-20', '2026-08-15', 1, 'LINK', 40, 2, 2, 4],
  ['TO12-20260816-0110-B001', 'TBC-ONE-12', '2026-08-16', 1, 'LINK', 32, 0, 0, 0],
  ['TB20-20260817-0111-B001', 'TBC-BOLD-20', '2026-08-17', 1, 'LINK', 0, 2, 18, 6],
  ['TB12-20260813-0106-B001', 'TBC-BOLD-12', '2026-08-13', 1, 'LINK', 18, 6, 0, 0],
  ['TX20-20260811-0104-B001', 'TBC-XTRA-20', '2026-08-11', 1, 'LINK', 102, 0, 0, 0],
  ['TB20T-20260814-0108-B001', 'TBC-BOLD-20-SKT', '2026-08-14', 2, 'BORONG', 11, 0, 0, 0],
  ['MI20-20260812-0105-B001', 'MILLIONS', '2026-08-12', 1, 'LINK', 36, 0, 12, 0],
  ['MI20-20260814-0107-B001', 'MILLIONS', '2026-08-14', 1, 'LINK', 16, 0, 0, 0],
  ['AM12-20260808-0102-B001', 'ARUM-MANIS', '2026-08-08', 1, 'BORONG', 75, 0, 0, 0],
  ['BR20-20260805-0101-B001', 'BRIGHTON-RED', '2026-08-05', 2, 'LINK', 22, 0, 0, 7]
]

export const equivalentPack = (sku: string, k: number, b: number, s: number, p: number) => {
  const per = packPer(sku)
  return k * per.karton + b * per.bal + s * per.slop + p
}

export const batchId = (no: string) =>
  seedId(SEED_GROUP.fgBatch, [...BATCHES.map(b => b.no), ...HISTORIC.map(h => h[0])].indexOf(no) + 1)
const machineIdOf = (code?: string) =>
  code
    ? seedId(
        SEED_GROUP.machine,
        ['MOLLINS-1', 'MOLLINS-2', 'PROTOS-1', 'PROTOS-2', 'SASIB', 'PRO-MILD'].indexOf(code) + 1
      )
    : null

/** Pecah nomor batch `TB20T-20260818-0119-B001` → kepala label. */
export const labelHead = (no: string) => {
  const [short, date, seq] = no.split('-')
  return { short, date, seq }
}
export const clock = (startHour: number, startMinute: number, offsetMinutes: number) => {
  const total = startHour * 60 + startMinute + offsetMinutes
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}${String(total % 60).padStart(2, '0')}00`
}

export function buildWmsFgSeed(): SeedTables {
  const fg_labeler_batch: LocalRow[] = []
  const fg_labeler_feed_log: LocalRow[] = []
  const fg_label: LocalRow[] = []
  const wxl_submission: LocalRow[] = []
  const wxl_submission_log: LocalRow[] = []
  let labelNo = 0
  let logNo = 0

  const pushLabel = (
    batch: string,
    sku: string,
    level: 'Karton' | 'Bal' | 'Slop',
    seqNo: number,
    at: number,
    status: string
  ) => {
    labelNo += 1
    const head = labelHead(batch)
    const letter = level === 'Karton' ? 'C' : level === 'Bal' ? 'B' : 'S'
    const row: LocalRow = {
      ...audit(ulil, '2026-08-18 14:00'),
      id: seedId(SEED_GROUP.fgLabel, labelNo),
      label_no: `JLC:${letter}:F:${head.short}:${head.seq}:${head.date}:${clock(6, 0, at)}:B001:${String(seqNo).padStart(4, '0')}`,
      sku_id: fgSkuId(sku),
      batch_id: batchId(batch),
      level,
      gtin: null,
      parent_label_id: null,
      zone_id: null,
      pallet_id: null,
      container_id: null,
      label_status: status,
      is_priority_lot: false
    }
    fg_label.push(row)
    return row
  }

  BATCHES.forEach((batch, index) => {
    const id = batchId(batch.no)
    const submissionId = seedId(SEED_GROUP.wxlSubmission, 900 + index + 1)
    const eq = equivalentPack(batch.sku, batch.k, batch.b, batch.s, batch.p)
    fg_labeler_batch.push({
      ...audit(ulil, batch.receivedAt),
      id,
      batch_no: batch.no,
      labeler_ref: batch.labelerRef ?? null,
      sku_id: fgSkuId(batch.sku),
      prod_date: batch.prodDate,
      shift: String(batch.shift),
      source: batch.source,
      machine_id: machineIdOf(batch.machine),
      spk_ref: null,
      qty_karton: batch.k,
      qty_bal: batch.b,
      qty_slop: batch.s,
      qty_pack: batch.p,
      qty_karton_actual: null,
      qty_bal_actual: null,
      qty_slop_actual: null,
      qty_pack_actual: null,
      equivalent_pack: eq,
      label_ok: null,
      checked_at: null,
      status_id: WF_FG.statusId(batch.status),
      submission_id: submissionId
    })
    wxl_submission.push({
      ...audit(ulil, batch.receivedAt),
      id: submissionId,
      workflow_id: WF_FG.workflowId,
      entity: 'fg_labeler_batch',
      entity_id: id,
      source: 'ACTION',
      current_status_id: WF_FG.statusId(batch.status),
      last_action_id: batch.steps ? WF_FG.actionId(batch.steps[batch.steps.length - 1][0]) : null,
      workflow_snapshot: null
    })
    ;(batch.steps ?? []).forEach(([action, actor, by, at, reason]) => {
      const [from, to] = WF_FG.actionStatus[action]
      logNo += 1
      wxl_submission_log.push({
        ...audit(USER_IDS[by], at),
        id: seedId(SEED_GROUP.wxlSubmissionLog, 9000 + logNo),
        workflow_id: WF_FG.workflowId,
        submission_id: submissionId,
        action_id: WF_FG.actionId(action),
        actor_id: WF_FG.actorId(actor),
        from_status_id: WF_FG.statusId(from),
        to_status_id: WF_FG.statusId(to),
        approval_total_count: 1,
        approval_approved_count: 1,
        approval_rejected_count: 0,
        reason: reason ?? null,
        entity: 'fg_labeler_batch',
        entity_id: id
      })
    })
    if (batch.labelerRef?.startsWith('ALF-'))
      fg_labeler_feed_log.push({
        ...audit(null, batch.receivedAt),
        id: seedId(SEED_GROUP.fgFeedLog, index + 1),
        external_batch_id: batch.labelerRef,
        batch_id: id,
        raw_payload: { sku: batch.sku, shift: batch.shift, machine: batch.machine, k: batch.k, b: batch.b, s: batch.s },
        validation_result: 'OK',
        error_message: null,
        received_at: utc(batch.receivedAt)
      })
    if (batch.status === 'FG_IN_PRODUCTION') return
    /* Pohon nomor QR: Karton + Bal lepas + Slop lepas (nomor Bal/Slop di dalam Karton dihitung dari konversi). */
    const base = 500 + index * 100
    for (let i = 0; i < batch.k; i++) pushLabel(batch.no, batch.sku, 'Karton', base + i + 1, 27 + i * 7, 'LABELED')
    for (let i = 0; i < batch.b; i++)
      pushLabel(batch.no, batch.sku, 'Bal', 2030 + index * 10 + i + 1, 297 + i * 7, 'LABELED')
    for (let i = 0; i < batch.s; i++)
      pushLabel(batch.no, batch.sku, 'Slop', 41978 + index * 10 + i + 1, 93 + i * 7, 'LABELED')
  })

  HISTORIC.forEach(([no, sku, prodDate, shift, source, k, b, s, p], index) => {
    const id = batchId(no)
    const submissionId = seedId(SEED_GROUP.wxlSubmission, 950 + index + 1)
    fg_labeler_batch.push({
      ...audit(ulil, `${prodDate} 15:00`),
      id,
      batch_no: no,
      labeler_ref: null,
      sku_id: fgSkuId(sku),
      prod_date: prodDate,
      shift: String(shift),
      source,
      machine_id: null,
      spk_ref: null,
      qty_karton: k,
      qty_bal: b,
      qty_slop: s,
      qty_pack: p,
      qty_karton_actual: k,
      qty_bal_actual: b,
      qty_slop_actual: s,
      qty_pack_actual: p,
      equivalent_pack: equivalentPack(sku, k, b, s, p),
      label_ok: true,
      checked_at: utc(`${prodDate} 16:00`),
      status_id: WF_FG.statusId('FG_PUTAWAY_COMPLETED'),
      submission_id: submissionId
    })
    wxl_submission.push({
      ...audit(ulil, `${prodDate} 15:00`),
      id: submissionId,
      workflow_id: WF_FG.workflowId,
      entity: 'fg_labeler_batch',
      entity_id: id,
      source: 'ACTION',
      current_status_id: WF_FG.statusId('FG_PUTAWAY_COMPLETED'),
      last_action_id: WF_FG.actionId('ACT_FG_COMPLETE'),
      workflow_snapshot: null
    })
    logNo += 1
    wxl_submission_log.push({
      ...audit(budi, `${prodDate} 18:00`),
      id: seedId(SEED_GROUP.wxlSubmissionLog, 9000 + logNo),
      workflow_id: WF_FG.workflowId,
      submission_id: submissionId,
      action_id: WF_FG.actionId('ACT_FG_COMPLETE'),
      actor_id: WF_FG.actorId('ACT_FG_OP_FG'),
      from_status_id: WF_FG.statusId('FG_ASSIGNED'),
      to_status_id: WF_FG.statusId('FG_PUTAWAY_COMPLETED'),
      approval_total_count: 1,
      approval_approved_count: 1,
      approval_rejected_count: 0,
      reason: null,
      entity: 'fg_labeler_batch',
      entity_id: id
    })
  })

  /* ---- tugas putaway seed: sisa bongkar BPO-260818-018 (3 Bal + 16 Slop), operator Budi ---- */
  const origin = 'TO12-20260816-0110-B001'
  const taskId = seedId(SEED_GROUP.fgPutawayTask, 1)
  const items: LocalRow[] = []
  const unitsOf: ['Bal' | 'Slop', number, number][] = [
    ['Bal', 3, 1389],
    ['Slop', 16, 27502]
  ]
  unitsOf.forEach(([level, count, firstNo]) => {
    for (let i = 0; i < count; i++) {
      const label = pushLabel(origin, 'TBC-ONE-12', level, firstNo + i, 60, 'ASSIGNED')
      items.push({
        ...audit(ulil, '2026-08-18 14:55'),
        id: seedId(SEED_GROUP.fgPutawayItem, items.length + 1),
        task_id: taskId,
        unit_type: level,
        target_pallet_id: level === 'Bal' ? palletId('D1-P3') : null,
        target_container_id: level === 'Slop' ? containerId('CT-A') : null,
        qty: 1,
        override_reason: null,
        label_id: label.id,
        done_qty: 0,
        actual_pallet_id: null,
        actual_container_id: null,
        item_status: 'PENDING'
      })
    }
  })
  const fg_putaway_task: LocalRow[] = [
    {
      ...audit(ulil, '2026-08-18 14:55'),
      id: taskId,
      fpw_no: 'FPW-260818-011',
      batch_id: batchId(origin),
      operator_id: budi,
      task_status: 'ASSIGNED',
      source_ref: 'BPO-260818-018',
      note: 'Sisa bongkar'
    }
  ]

  const pallet: LocalRow[] = PALLETS.map(code => {
    const opening = OPENING_PALLETS.find(([c]) => c === code)
    return {
      ...audit(ulil, '2026-08-01 07:00'),
      id: palletId(code),
      code,
      pallet_kind: 'FG',
      zone_id: zoneId(code.split('-')[0]),
      capacity: 24,
      recommended: 18,
      unit_type: opening ? (opening[3] ?? 'Karton') : null,
      description: `Pallet FG ${code}`,
      pallet_status: opening ? 'IN_USE' : 'EMPTY',
      cycle_count: 0,
      is_active: true,
      sku_id: opening ? fgSkuId(opening[1]) : null,
      occupied: opening ? opening[2] : 0
    }
  })

  return {
    mt_product: EXTRA_SKUS.map(([code, name, type, btg, balK, slopB], index) => ({
      ...audit(ulil, '2026-08-01 07:00'),
      id: seedId(SEED_GROUP.sku, 20 + index),
      code,
      name,
      type,
      hierarchy: 'Karton>Bal>Slop>Pack',
      pack_per_slop: 10,
      slop_per_bal: slopB,
      bal_per_karton: balK,
      btg_per_pack: btg,
      has_bal: true,
      is_active: true
    })),
    warehouse_zone: [...ZONE_CODES, FG_STAGING_ZONE_CODE].map(code => ({
      ...audit(ulil, '2026-08-01 07:00'),
      id: zoneId(code),
      code,
      name: code === FG_STAGING_ZONE_CODE ? 'Area Staging FG' : `Zone ${code}`,
      warehouse_id: FG_WAREHOUSE_ID,
      zone_type: code === FG_STAGING_ZONE_CODE ? 'STAGING_FG' : 'STORAGE_FG'
    })),
    pallet,
    container: CONTAINERS.map(([code, name, type]) => ({
      ...audit(ulil, '2026-08-01 07:00'),
      id: containerId(code),
      code,
      name,
      container_type: type,
      zone_id: null,
      is_active: true
    })),
    fg_labeler_batch,
    fg_labeler_feed_log,
    fg_label,
    fg_putaway_task,
    fg_putaway_task_item: items,
    fg_pack_stock: [],
    wxl_submission,
    wxl_submission_log
  }
}
